import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Circle } from 'lucide-react';
import { api, type AgentSchedule } from '@/lib/api';
import { useAuth } from '@/contexts/auth-context';
import { useTranslations } from '@/i18n/use-translations';

export type StepState = 'done' | 'todo' | 'warn';

export interface SetupStep {
  key: 'provider' | 'personality' | 'connections' | 'rituals' | 'tested';
  state: StepState;
  href: string;
  /** i18n key under agents.setup + values */
  label: string;
  values?: Record<string, unknown>;
}

export interface SetupData {
  providerCount: number | null;
  hasPersonality: boolean;
  connections: { connected: number; total: number } | null;
  enabledRituals: number | null;
  autonomyEnabled: boolean;
  tested: boolean;
}

const TESTED_KEY = (agentId: string) => `fyso-agent-tested:${agentId}`;
const HIDDEN_KEY = (agentId: string) => `fyso-agent-setup-hidden:${agentId}`;

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, on: boolean) {
  try {
    if (on) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // private mode
  }
}

/** Probar calls this after the first message or call. */
export function markAgentTested(agentId: string) {
  writeFlag(TESTED_KEY(agentId), true);
  window.dispatchEvent(new CustomEvent('fyso:agent-tested', { detail: agentId }));
}

export function buildSetupSteps(agentId: string, d: SetupData): SetupStep[] {
  const base = `/agents/${agentId}`;
  return [
    {
      key: 'provider',
      state: d.providerCount === null ? 'todo' : d.providerCount > 0 ? 'done' : 'warn',
      href: '/settings/ai-provider',
      label: 'provider',
    },
    { key: 'personality', state: d.hasPersonality ? 'done' : 'todo', href: `${base}/behavior`, label: 'personality' },
    {
      key: 'connections',
      state: d.connections && d.connections.connected > 0 ? 'done' : 'todo',
      href: `${base}/connections`,
      label: d.connections ? 'connectionsCount' : 'connections',
      values: d.connections ?? undefined,
    },
    {
      key: 'rituals',
      state: !d.autonomyEnabled && (d.enabledRituals ?? 0) > 0 ? 'warn' : (d.enabledRituals ?? 0) > 0 ? 'done' : 'todo',
      href: `${base}/rituals`,
      label: !d.autonomyEnabled && (d.enabledRituals ?? 0) > 0 ? 'ritualsPaused' : (d.enabledRituals ?? 0) > 0 ? 'ritualsCount' : 'rituals',
      values: { count: d.enabledRituals ?? 0 },
    },
    { key: 'tested', state: d.tested ? 'done' : 'todo', href: `${base}/test`, label: 'tested' },
  ];
}

const ICON: Record<StepState, { icon: React.ElementType; className: string }> = {
  done: { icon: CheckCircle2, className: 'text-green-600 dark:text-green-400' },
  todo: { icon: Circle, className: 'text-muted-foreground' },
  warn: { icon: AlertTriangle, className: 'text-amber-600 dark:text-amber-400' },
};

interface SetupChecklistProps {
  agent: { id: string; slug: string; name: string; systemPrompt?: string | null; soul?: string | null; settings?: { autonomy?: { enabled?: boolean } } | null };
  schedules: AgentSchedule[] | null;
  /** Re-fetch trigger (the layout passes the pathname so tab changes refresh it). */
  refreshKey: string;
  highlight?: boolean;
}

/** "Puesta en marcha": what the agent still needs, each step linking to its tab. */
export function SetupChecklist({ agent, schedules, refreshKey, highlight = false }: SetupChecklistProps) {
  const t = useTranslations('agents.setup');
  const { tenant } = useAuth();
  const [providerCount, setProviderCount] = useState<number | null>(null);
  const [connections, setConnections] = useState<SetupData['connections']>(null);
  const [tested, setTested] = useState(() => readFlag(TESTED_KEY(agent.id)));
  const [hidden, setHidden] = useState(() => readFlag(HIDDEN_KEY(agent.id)));

  useEffect(() => {
    if (!tenant?.id) return;
    api.tenantAiProviders.list(tenant.id)
      .then((list) => setProviderCount(Array.isArray(list) ? list.length : 0))
      .catch(() => setProviderCount(null));
  }, [tenant?.id, refreshKey]);

  useEffect(() => {
    api.aiAgents.connections(agent.slug)
      .then((d) => setConnections({
        connected: d.integrations.filter((i) => i.connection).length,
        total: d.integrations.length,
      }))
      .catch(() => setConnections(null));
  }, [agent.slug, refreshKey]);

  useEffect(() => {
    setTested(readFlag(TESTED_KEY(agent.id)));
    const onTested = (e: Event) => {
      if ((e as CustomEvent).detail === agent.id) setTested(true);
    };
    window.addEventListener('fyso:agent-tested', onTested);
    return () => window.removeEventListener('fyso:agent-tested', onTested);
  }, [agent.id, refreshKey]);

  const steps = buildSetupSteps(agent.id, {
    providerCount,
    hasPersonality: !!(agent.systemPrompt?.trim() || agent.soul?.trim()),
    connections,
    enabledRituals: schedules ? schedules.filter((s) => s.kind !== 'followup' && s.enabled).length : null,
    autonomyEnabled: agent.settings?.autonomy?.enabled !== false,
    tested,
  });
  const done = steps.every((s) => s.state === 'done');
  // Keep the preparation state visible on first view, even after every step is done.
  // It can still be hidden explicitly and the preference remains per agent.
  const collapsed = hidden;

  const toggle = () => {
    writeFlag(HIDDEN_KEY(agent.id), !hidden);
    setHidden(!hidden);
  };

  if (collapsed) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm" data-testid="setup-checklist" data-collapsed="true">
        {done ? (
          <span className="inline-flex items-center gap-1.5 text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            {t('ready', { name: agent.name })}
          </span>
        ) : (
          <span className="text-muted-foreground">
            {t('pending', { count: steps.filter((s) => s.state !== 'done').length })}
          </span>
        )}
        {!done && (
          <button type="button" onClick={toggle} className="inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground">
            <ChevronDown className="h-3 w-3" aria-hidden />
            {t('show')}
          </button>
        )}
      </div>
    );
  }

  return (
    <nav
      aria-label={t('title')}
      data-testid="setup-checklist"
      className={`mt-3 rounded-md border px-3 py-2 ${highlight ? 'border-primary/50 bg-primary/5' : 'bg-muted/20'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('title')}</p>
        <button type="button" onClick={toggle} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ChevronUp className="h-3 w-3" aria-hidden />
          {t('hide')}
        </button>
      </div>
      <ol className="mt-1.5 flex gap-x-4 gap-y-1.5 overflow-x-auto pb-1 sm:flex-wrap">
        {steps.map((step) => {
          const { icon: Icon, className } = ICON[step.state];
          return (
            <li key={step.key} className="shrink-0">
              <Link
                to={step.href}
                className="inline-flex items-center gap-1.5 rounded-sm text-sm hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                data-state={step.state}
              >
                <Icon className={`h-4 w-4 shrink-0 ${className}`} aria-hidden />
                <span>{t(`steps.${step.label}`, step.values)}</span>
                <span className="sr-only">{t(`state.${step.state}`)}</span>
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
